import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { API_VERSION_QUERY_PARAM_NAME, containerElementId } from '../constants';
import { initFromInitialData } from '../dynamic-data';
import { resolveComponentAndChildComponents } from '../resolve-component';
import { initializeHistorySync } from '../current-page/history-sync';
import { setUpdateIsUsignHydration } from '../update-is-using-hydration';
import { isHash, scrollToHash, setHashScrollOffset } from '../scrolling';
import { currentUrl } from '../url';
import { replaceState } from './history';
import { getBreakpoint } from '../match-device-width';
import { findPageAndCacheIfAny } from '../current-page/state';

let resolveInitialRenderDone: (value?: unknown) => unknown;
export const initialRenderDone = new Promise<unknown>(resolve => (resolveInitialRenderDone = resolve));

export type ClientBootstapConfig = {
  hashScrollOffset?: (deviceWidth: number) => number;
};

export async function bootstrap(app: React.ReactNode, config?: ClientBootstapConfig) {
  initializeHistorySync();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bootstrapApp(config, app);
    });
  } else {
    bootstrapApp(config, app);
  }
}
async function bootstrapApp(config: ClientBootstapConfig | undefined, app: React.ReactNode) {
  const url = currentUrl();
  if (url.searchParams.has(API_VERSION_QUERY_PARAM_NAME)) {
    url.searchParams.delete(API_VERSION_QUERY_PARAM_NAME);
    replaceState(url);
  }

  await resolveComponentAndChildComponents(window.INITIAL_DATA);
  findPageAndCacheIfAny(window.INITIAL_DATA.page);

  const initialData: Excite.ContentApi.ClientData = window.INITIAL_DATA;
  initFromInitialData(initialData);

  const containerElement = document.getElementById(containerElementId);

  if (!containerElement) {
    throw new Error(`Container element with id: "${containerElementId}", could not be found.`);
  }

  const matchSSRBreakpoint = window.SSR_BREAKPOINT === getBreakpoint();

  if (!matchSSRBreakpoint) {
    console.info(
      'Render instead of hydrate because device width on server render did not match device width on client',
    );
  }
  if (config?.hashScrollOffset) {
    setHashScrollOffset(config.hashScrollOffset(window.innerWidth));
  }

  const hydrate = window.IS_SERVER_SIDE_RENDERED && matchSSRBreakpoint;
  setUpdateIsUsignHydration(hydrate);

  if (hydrate) {
    ReactDOMClient.hydrateRoot(containerElement, app);
  } else {
    const root = ReactDOMClient.createRoot(containerElement);
    root.render(app);
  }

  await resolveInitialRenderDone();

  const hash = currentUrl().hash;
  if (isHash(hash)) {
    if (document.readyState === 'complete') {
      scrollToHash(hash);
    } else {
      document.addEventListener('readystatechange', () => {
        if (document.readyState === 'complete') {
          scrollToHash(hash);
        }
      });
    }
  }
}
